import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { RelatedNews } from "@/components/RelatedNews";
import {
  ArrowLeft, User, Shield, Calendar,
  Trophy, TrendingUp, CheckCircle2, AlertCircle, HelpCircle,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

const POSITION_LABEL = { GK: "Goalkeeper", DEF: "Defender", MID: "Midfielder", FWD: "Forward" };

// Simple country → emoji flag helper. Supports the common ABFA-adjacent
// nationalities and falls back to a generic globe. This keeps the UI dependency
// footprint zero — no flag CDN.
const COUNTRY_FLAGS = {
  "antigua and barbuda": "🇦🇬",
  "antigua & barbuda": "🇦🇬",
  "antigua": "🇦🇬",
  "england": "🏴󠁧󠁢󠁥󠁮󠁧󠁿",
  "united kingdom": "🇬🇧",
  "united states": "🇺🇸",
  "usa": "🇺🇸",
  "trinidad and tobago": "🇹🇹",
  "jamaica": "🇯🇲",
  "barbados": "🇧🇧",
  "grenada": "🇬🇩",
  "saint kitts and nevis": "🇰🇳",
  "saint lucia": "🇱🇨",
  "dominica": "🇩🇲",
  "guyana": "🇬🇾",
  "haiti": "🇭🇹",
  "canada": "🇨🇦",
  "brazil": "🇧🇷",
  "france": "🇫🇷",
  "spain": "🇪🇸",
  "portugal": "🇵🇹",
  "germany": "🇩🇪",
  "netherlands": "🇳🇱",
  "argentina": "🇦🇷",
  "colombia": "🇨🇴",
  "nigeria": "🇳🇬",
  "ghana": "🇬🇭",
};

const flagFor = (nationality) => {
  if (!nationality) return "🌐";
  return COUNTRY_FLAGS[nationality.trim().toLowerCase()] || "🌐";
};

const DataQualityIcon = ({ status }) => {
  if (status === "VERIFIED") return <CheckCircle2 className="w-3.5 h-3.5 text-[#009E60]" />;
  if (status === "CONFLICT") return <AlertCircle className="w-3.5 h-3.5 text-red-500" />;
  if (status === "MISSING") return <HelpCircle className="w-3.5 h-3.5 text-gray-400" />;
  return null;
};

/**
 * KeyDetailRow — editorial two-column row matching the classic player card
 * layout (Nationality, Position, Height, Weight, Current Team, Birthday, Age).
 */
const KeyDetailRow = ({ label, value, quality, isLast }) => (
  <div className={`flex items-center py-3 ${isLast ? "" : "border-b border-gray-200"}`}>
    <div className="w-1/2 text-sm font-heading font-bold text-[#09090b] uppercase tracking-wide">{label}</div>
    <div className="w-1/2 text-sm text-gray-700 flex items-center gap-2">
      {value ?? <span className="text-gray-400 italic">Not available</span>}
      <DataQualityIcon status={quality} />
    </div>
  </div>
);

const calcAge = (dob) => {
  if (!dob) return null;
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return null;
  const diff = Date.now() - birth.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
};

const formatDate = (d) => {
  if (!d) return null;
  const date = new Date(d);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const PlayerDetailPage = () => {
  const { playerId } = useParams();
  const [career, setCareer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/players/${playerId}/career`);
        if (cancelled) return;
        setCareer(data);
      } catch (e) {
        console.error("Player career fetch failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [playerId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-16 px-4">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (!career?.player) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold text-gray-500">Player Not Found</h2>
          <Link to="/players" className="text-[#009E60] hover:underline mt-4 inline-block" data-testid="link-back-players">
            Back to Players
          </Link>
        </div>
      </div>
    );
  }

  const { player, club_history, seasons, totals, current_teams } = career;
  const teams = current_teams || [];
  const primaryTeam = teams.find((t) => t.is_primary) || teams[0] || null;
  const additionalTeams = teams.filter((t) => t.team_id !== primaryTeam?.team_id);
  const dq = player.data_quality || {};
  const age = calcAge(player.date_of_birth);

  return (
    <div data-testid="player-detail-page" className="bg-gray-50">
      {/* HERO */}
      <section className="bg-gradient-to-br from-[#09090b] via-[#0f0f14] to-[#1a1a24] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <Link
            to="/players"
            className="inline-flex items-center text-gray-400 hover:text-white text-sm mb-6 transition-colors"
            data-testid="link-back-players"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Players
          </Link>

          <div className="flex flex-col md:flex-row items-center md:items-end gap-8">
            {/* Photo */}
            <div className="relative">
              <div className="w-40 h-40 md:w-52 md:h-52 rounded-full bg-white/5 ring-4 ring-[#FFC72C] overflow-hidden flex items-center justify-center">
                <img
                  src={player.photo_url || "https://placehold.co/400x400/09090b/FFF?text=PLAYER"}
                  alt={player.name}
                  className="w-full h-full object-cover"
                />
              </div>
              {player.number != null && (
                <div className="absolute -bottom-2 -right-2 w-16 h-16 bg-[#FFC72C] text-black flex items-center justify-center font-heading text-3xl font-black">
                  {player.number}
                </div>
              )}
            </div>

            {/* Identity */}
            <div className="flex-1 text-center md:text-left">
              <div className="flex flex-wrap items-center gap-2 justify-center md:justify-start mb-3">
                {player.registration_id && (
                  <Badge className="bg-[#FFC72C] text-black font-mono hover:bg-[#FFC72C]" data-testid="badge-registration-id">
                    {player.registration_id}
                  </Badge>
                )}
                {player.position && (
                  <Badge variant="outline" className="border-white/20 text-white">
                    {POSITION_LABEL[player.position] || player.position}
                  </Badge>
                )}
              </div>
              <h1 className="font-heading text-4xl md:text-6xl font-black uppercase tracking-tight" data-testid="text-player-name">
                {player.name}
              </h1>
              <div className="flex flex-wrap items-center gap-4 mt-4 text-gray-300 justify-center md:justify-start text-sm">
                {teams.map((t) => (
                  <Link
                    key={t.team_id}
                    to={`/teams/${t.team_id}`}
                    className="flex items-center gap-2 hover:text-[#FFC72C] transition-colors"
                    data-testid={t.is_primary ? "link-current-club" : `link-team-${t.team_id}`}
                  >
                    <Shield className="w-4 h-4" />
                    <span className="font-semibold">{t.name || "Unknown Team"}</span>
                    {!t.is_primary && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-white/10 uppercase tracking-widest text-gray-300">
                        Also
                      </span>
                    )}
                  </Link>
                ))}
                {player.nationality && (
                  <span className="flex items-center gap-1.5" data-testid="hero-nationality">
                    <span className="text-lg" aria-hidden>{flagFor(player.nationality)}</span>
                    {player.nationality}
                  </span>
                )}
                {age != null && (
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    {age} yrs
                  </span>
                )}
              </div>
            </div>

            {/* Career totals */}
            <div className="grid grid-cols-3 gap-3 md:gap-6 pt-4 md:pt-0">
              {[
                { label: "Clubs", value: totals?.clubs ?? 0 },
                { label: "Apps", value: totals?.appearances ?? 0 },
                { label: "Goals", value: totals?.goals ?? 0 },
              ].map((s) => (
                <div key={s.label} className="text-center min-w-[70px]" data-testid={`stat-${s.label.toLowerCase()}`}>
                  <div className="font-heading text-3xl md:text-4xl font-black text-[#FFC72C]">{s.value}</div>
                  <div className="text-[10px] md:text-xs uppercase tracking-widest text-gray-400">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TABS */}
      <section className="py-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="w-full justify-start border-b border-gray-200 mb-8 bg-transparent h-auto p-0 overflow-x-auto">
              {["overview", "career", "clubs", "statistics"].map((t) => (
                <TabsTrigger
                  key={t}
                  value={t}
                  className="px-5 md:px-6 py-4 font-heading font-bold uppercase text-gray-500 data-[state=active]:text-[#09090b] data-[state=active]:border-b-4 data-[state=active]:border-[#FFC72C] rounded-none bg-transparent whitespace-nowrap"
                  data-testid={`tab-${t}`}
                >
                  {t}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* OVERVIEW — editorial card + Primary League table */}
            <TabsContent value="overview">
              {/* Big Number + Name header (Sofascore/Wikipedia editorial style) */}
              <div className="flex items-center gap-4 mb-6" data-testid="player-editorial-header">
                {player.number != null && (
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-[#009E60] text-white flex items-center justify-center font-heading text-3xl md:text-4xl font-black shrink-0">
                    {player.number}
                  </div>
                )}
                <h2 className="font-heading text-3xl md:text-5xl font-black text-[#09090b] tracking-tight truncate">
                  {player.name.split(" ")[0]}
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Photo card */}
                <div className="bg-gray-50 aspect-[4/5] flex items-center justify-center overflow-hidden">
                  <img
                    src={player.photo_url || "https://placehold.co/400x500/e5e7eb/09090b?text=PLAYER"}
                    alt={player.name}
                    className="w-full h-full object-cover"
                    data-testid="player-editorial-photo"
                  />
                </div>

                {/* Key details table */}
                <div className="bg-gray-50 p-6 md:p-8" data-testid="player-key-details">
                  <KeyDetailRow
                    label="Nationality"
                    value={
                      player.nationality && (
                        <>
                          <span className="text-lg" aria-hidden>{flagFor(player.nationality)}</span>
                          <span>{player.nationality}</span>
                        </>
                      )
                    }
                    quality={dq.nationality}
                  />
                  <KeyDetailRow label="Position" value={POSITION_LABEL[player.position] || player.position} quality={dq.position} />
                  <KeyDetailRow label="Height" value={player.height} quality={dq.height} />
                  <KeyDetailRow label="Weight" value={player.weight} quality={dq.weight} />
                  <KeyDetailRow
                    label="Current Team"
                    value={primaryTeam ? (
                      <div className="flex flex-col gap-1">
                        <Link to={`/teams/${primaryTeam.team_id}`} className="hover:text-[#009E60] transition-colors font-semibold">
                          {primaryTeam.name || "Unknown Team"}
                        </Link>
                        {additionalTeams.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-1" data-testid="also-teams">
                            {additionalTeams.map((t) => (
                              <Link
                                key={t.team_id}
                                to={`/teams/${t.team_id}`}
                                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-[#009E60]/10 text-[#009E60] hover:bg-[#009E60] hover:text-white transition-colors"
                                data-testid={`also-team-link-${t.team_id}`}
                              >
                                <Shield className="w-3 h-3" />
                                {t.name || "Team"}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : null}
                  />
                  <KeyDetailRow label="Birthday" value={formatDate(player.date_of_birth)} quality={dq.date_of_birth} />
                  <KeyDetailRow label="Age" value={age != null ? `${age}` : null} isLast />
                </div>
              </div>

              {/* Primary League table (seasons filtered to current club is optional; show all) */}
              <div className="bg-white border border-gray-100" data-testid="player-primary-league">
                <div className="bg-[#009E60] text-white px-6 py-3">
                  <h3 className="font-heading font-black uppercase tracking-widest text-sm">
                    Primary League
                  </h3>
                </div>
                {seasons?.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 uppercase text-xs">
                        <tr className="text-left">
                          <th className="px-4 md:px-6 py-3 font-heading font-bold">Season</th>
                          <th className="px-4 md:px-6 py-3 font-heading font-bold">Team</th>
                          <th className="px-4 md:px-6 py-3 font-heading font-bold text-center">Goals</th>
                          <th className="px-4 md:px-6 py-3 font-heading font-bold text-center">Assists</th>
                          <th className="px-4 md:px-6 py-3 font-heading font-bold text-center">Yellow Cards</th>
                          <th className="px-4 md:px-6 py-3 font-heading font-bold text-center">Red Cards</th>
                        </tr>
                      </thead>
                      <tbody>
                        {seasons.map((s) => (
                          <tr key={s.season_stat_id} className="border-t border-gray-100 hover:bg-gray-50 transition-colors" data-testid={`primary-league-row-${s.season_stat_id}`}>
                            <td className="px-4 md:px-6 py-3 font-heading font-bold">{s.season}</td>
                            <td className="px-4 md:px-6 py-3">{s.club_name || "—"}</td>
                            <td className="px-4 md:px-6 py-3 text-center font-heading font-bold">{s.goals ?? 0}</td>
                            <td className="px-4 md:px-6 py-3 text-center">{s.assists ?? 0}</td>
                            <td className="px-4 md:px-6 py-3 text-center">{s.yellow_cards ?? 0}</td>
                            <td className="px-4 md:px-6 py-3 text-center">{s.red_cards ?? 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="px-6 py-8 text-gray-400 italic text-sm text-center">
                    No season statistics recorded yet. Stats will appear here once match data is entered.
                  </div>
                )}
              </div>
            </TabsContent>

            {/* CAREER */}
            <TabsContent value="career">
              <div className="bg-white p-6 border border-gray-100">
                <h3 className="font-heading text-xl font-bold uppercase mb-6 border-l-4 border-[#FFC72C] pl-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" /> Career Timeline
                </h3>
                {club_history?.length ? (
                  <ol className="relative border-l-2 border-gray-200 ml-3 space-y-8">
                    {club_history.map((h) => (
                      <li key={h.history_id} className="ml-6" data-testid={`career-entry-${h.history_id}`}>
                        <span className="absolute -left-2.5 flex items-center justify-center w-5 h-5 bg-[#FFC72C] rounded-full ring-4 ring-white">
                          <Shield className="w-3 h-3 text-black" />
                        </span>
                        <div className="flex flex-wrap items-center gap-3 mb-1">
                          {h.club_logo && (
                            <img src={h.club_logo} alt="" className="w-8 h-8 object-contain" />
                          )}
                          <Link
                            to={`/teams/${h.club_id}`}
                            className="font-heading font-bold text-lg uppercase hover:text-[#009E60] transition-colors"
                          >
                            {h.club_name || "Club"}
                          </Link>
                          <Badge variant="outline" className="text-[10px] uppercase">
                            {h.transfer_type || "transfer"}
                          </Badge>
                          {h.to_date === null && (
                            <Badge className="bg-[#009E60] text-white text-[10px] uppercase hover:bg-[#009E60]">
                              Current
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {formatDate(h.from_date) || "—"} → {h.to_date ? formatDate(h.to_date) : "Present"}
                        </p>
                        {h.notes && <p className="text-xs text-gray-400 mt-1">{h.notes}</p>}
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="text-gray-400 italic">No career history recorded yet.</p>
                )}
              </div>
            </TabsContent>

            {/* CLUBS */}
            <TabsContent value="clubs">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {club_history?.length ? (
                  Array.from(
                    new Map(club_history.map((h) => [h.club_id, h])).values()
                  ).map((h) => (
                    <Link
                      key={h.club_id}
                      to={`/teams/${h.club_id}`}
                      className="bg-white p-4 border border-gray-100 hover:border-[#FFC72C] transition-colors group"
                      data-testid={`club-card-${h.club_id}`}
                    >
                      <div className="aspect-square bg-gray-50 flex items-center justify-center p-4 mb-3">
                        <img
                          src={h.club_logo || "https://placehold.co/200x200/09090b/FFF?text=CLUB"}
                          alt=""
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <p className="font-heading font-bold uppercase text-sm group-hover:text-[#009E60] transition-colors">
                        {h.club_name || "Club"}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(h.from_date) || "—"} → {h.to_date ? formatDate(h.to_date) : "Present"}
                      </p>
                    </Link>
                  ))
                ) : (
                  <p className="text-gray-400 italic col-span-full">No clubs recorded yet.</p>
                )}
              </div>
            </TabsContent>

            {/* STATISTICS */}
            <TabsContent value="statistics">
              <div className="bg-white border border-gray-100 overflow-x-auto">
                <h3 className="font-heading text-xl font-bold uppercase p-6 pb-4 border-l-4 border-[#FFC72C] ml-0 flex items-center gap-2">
                  <Trophy className="w-5 h-5" /> Season-by-Season
                </h3>
                {seasons?.length ? (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 uppercase text-xs">
                      <tr>
                        <th className="text-left px-6 py-3">Season</th>
                        <th className="text-left px-6 py-3">Club</th>
                        <th className="text-center px-3 py-3">Apps</th>
                        <th className="text-center px-3 py-3">Goals</th>
                        <th className="text-center px-3 py-3">Assists</th>
                        <th className="text-center px-3 py-3">Yellow</th>
                        <th className="text-center px-3 py-3">Red</th>
                        <th className="text-center px-3 py-3">Mins</th>
                      </tr>
                    </thead>
                    <tbody>
                      {seasons.map((s) => (
                        <tr key={s.season_stat_id} className="border-t border-gray-100" data-testid={`season-row-${s.season_stat_id}`}>
                          <td className="px-6 py-3 font-heading font-bold">{s.season}</td>
                          <td className="px-6 py-3">{s.club_name || "—"}</td>
                          <td className="text-center px-3 py-3">{s.appearances ?? 0}</td>
                          <td className="text-center px-3 py-3 font-bold text-[#009E60]">{s.goals ?? 0}</td>
                          <td className="text-center px-3 py-3">{s.assists ?? 0}</td>
                          <td className="text-center px-3 py-3">{s.yellow_cards ?? 0}</td>
                          <td className="text-center px-3 py-3">{s.red_cards ?? 0}</td>
                          <td className="text-center px-3 py-3">{s.minutes_played ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-6 text-gray-400 italic">
                    No season statistics recorded yet. Statistics will appear here once match data is entered.
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
      <RelatedNews entity="player_id" id={playerId} title="Player News" />
    </div>
  );
};

export default PlayerDetailPage;
